/**
 * PreviewStep — Step 3 of the New Import wizard (FR-03.4).
 *
 * After a file is selected, shows parse-preview stats + a table of the first 5
 * rows with a "Showing first 5 rows of N contacts" footer. When the source is
 * `baby_chops` (NUCCE path) an "Auto-imported from ChopLink" badge is shown.
 * When the file has rows without a `/in/` LinkedIn URL, a "N rows skipped: no
 * LinkedIn URL" line says so (2026-09-28) — the worker drops and counts those
 * rows, and the preview reads the same count off the file before the upload.
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px scale.
 * No inline styles, no arbitrary `text-[Npx]`.
 */

import type { PreviewData } from "../parsePreview";

export interface PreviewStepProps {
  preview: PreviewData;
  fromBabyChops: boolean;
}

export function PreviewStep({ preview, fromBabyChops }: PreviewStepProps) {
  // Cap the visible columns so the table stays readable (full mapping is Step 4).
  const visibleColumns = preview.columns.slice(0, 6);

  return (
    <div data-testid="preview-step" className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <p className="text-label text-text">
          {preview.total.toLocaleString()} contacts detected
        </p>
        {fromBabyChops ? (
          <span
            data-testid="baby-chops-badge"
            className="rounded-md bg-accent-muted px-2 py-1 text-caption text-accent"
          >
            Auto-imported from ChopLink
          </span>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
        <table className="w-full text-caption">
          <thead className="bg-surface text-text-secondary">
            <tr>
              {visibleColumns.map((col) => (
                <th key={col} className="px-3 py-2 text-left font-semibold">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, i) => {
              // Prefer a stable per-row identity (LinkedIn URL / camelCase
              // linkedinUrl) over the array index; fall back to a column-join.
              const rowKey =
                row["LinkedIn URL"] ||
                row.linkedinUrl ||
                `${visibleColumns.map((c) => row[c]).join("|")}#${i}`;
              return (
                <tr key={rowKey} className="border-t border-border">
                  {visibleColumns.map((col) => (
                    <td key={col} className="px-3 py-2 text-text">
                      {row[col] || "—"}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p data-testid="preview-footer" className="text-caption text-text-secondary">
        Showing first {Math.min(5, preview.total).toLocaleString()} rows of{" "}
        {preview.total.toLocaleString()} contacts
      </p>

      {preview.skippedNoLinkedin > 0 ? (
        <p data-testid="preview-skipped-no-linkedin" className="text-caption text-warning-ink">
          {preview.skippedNoLinkedin.toLocaleString()}{" "}
          {preview.skippedNoLinkedin === 1 ? "row" : "rows"} skipped: no LinkedIn URL
        </p>
      ) : null}
    </div>
  );
}
